/**
 * RC Header - JavaScript functionality
 * Handles header height observer, mobile menu, and search overlay
 */

/* ==========================================================================
   Height Observer - Tracks header height for sticky calculations
   ========================================================================== */

class RCHeaderHeightObserver extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
  }

  connectedCallback() {
    this.variable = this.getAttribute('variable') || 'header';
    this.setupObserver();
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setupObserver() {
    const target = this.firstElementChild;
    if (!target) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize || entry.target.offsetHeight;
        // For fixed headers, offsetHeight still works; use scrollHeight as fallback
        const finalHeight = height || target.scrollHeight;
        document.documentElement.style.setProperty(`--rc-${this.variable}-height`, `${finalHeight}px`);

        // Reserve space unless: PDP, or first section has hero/image/video
        const isFixed = target.classList.contains('rc-header--sticky') && !target.classList.contains('rc-header--transparent');
        if (isFixed) {
          const isPDP = document.body.classList.contains('template-product');
          const main = document.querySelector('main#MainContent, main.content-for-layout');
          // First actual section — skip any non-section nodes (e.g. the visually-hidden <h1>)
          const firstSection = main?.querySelector(':scope > .shopify-section');
          const hasHero = firstSection?.querySelector('.rc-hero-slider, .rc-no-header-offset, [data-transparent-header], .rc-about__hero, video, [data-hero], .rc-lookbook__hero');
          if (isPDP || hasHero) {
            this.style.minHeight = '';
          } else {
            // 0 px es válido (cabecera plana en móvil, 29-sep-2026): solo se usa 20 si la variable no existe.
            const marginTopValue = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rc-header-margin-top'));
            const marginTop = Number.isFinite(marginTopValue) ? marginTopValue : 20;
            this.style.minHeight = `${finalHeight + marginTop * 2}px`;
          }
        }
      }
    });

    this.resizeObserver.observe(target);
  }
}

if (!customElements.get('rc-header-height-observer')) {
  customElements.define('rc-header-height-observer', RCHeaderHeightObserver);
}

/* ==========================================================================
   Mobile Menu
   ========================================================================== */

class RCMobileMenu extends HTMLElement {
  constructor() {
    super();
    this.toggle = null;
    this.backdrop = null;
    this.panel = null;
    this.isOpen = false;
  }

  connectedCallback() {
    this.backdrop = this.querySelector('.rc-mobile-menu__backdrop');
    this.panel = this.querySelector('.rc-mobile-menu__panel');

    // Find the toggle button in the header
    this.toggle = document.querySelector('[aria-controls="rc-mobile-menu"]');

    if (this.toggle) {
      this.toggle.addEventListener('click', this.handleToggle.bind(this));
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.close.bind(this));
    }

    // Close button inside panel
    const closeBtn = this.querySelector('[data-mobile-menu-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.close.bind(this));
    }

    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Submenu panel navigation (legacy)
    this.setupSubmenuNavigation();

    // Collapsible accordion navigation (EME Studios style)
    this.setupCollapsibles();

    // Open the Joy rewards experience directly from the editorial menu.
    const rewardsButton = this.querySelector('[data-rc-open-rewards]');
    if (rewardsButton) {
      rewardsButton.addEventListener('click', () => this.openRewards());
    }
  }

  setupSubmenuNavigation() {
    // Open submenu buttons
    const openButtons = this.querySelectorAll('[data-mobile-submenu-open]');
    openButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const submenuId = btn.getAttribute('data-mobile-submenu-open');
        const submenuPanel = this.querySelector(`[data-mobile-submenu="${submenuId}"]`);
        if (submenuPanel) {
          submenuPanel.classList.add('is-active');
        }
      });
    });

    // Close/back buttons
    const closeButtons = this.querySelectorAll('[data-mobile-submenu-close]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const submenuPanel = btn.closest('.rc-mobile-menu__submenu-panel');
        if (submenuPanel) {
          submenuPanel.classList.remove('is-active');
        }
      });
    });
  }

  handleToggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.removeAttribute('hidden');
    this.setAttribute('open', '');

    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'true');
    }

    // Add open class to header for styling
    const header = document.querySelector('.rc-header');
    if (header) {
      header.classList.add('rc-header--open');
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus first link
    requestAnimationFrame(() => {
      const firstLink = this.querySelector('a, button');
      if (firstLink) firstLink.focus();
    });
  }

  close() {
    this.isOpen = false;
    this.removeAttribute('open');

    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    // Remove open class from header
    const header = document.querySelector('.rc-header');
    if (header) {
      header.classList.remove('rc-header--open');
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Return focus to toggle
    if (this.toggle) this.toggle.focus();

    // Close all submenus and collapsibles
    this.closeAllSubmenus();
    this.closeAllCollapsibles();

    // Hide after transition (match 0.5s panel slide)
    setTimeout(() => {
      if (!this.isOpen) {
        this.setAttribute('hidden', '');
      }
    }, 500);
  }

  closeAllSubmenus() {
    const activeSubmenus = this.querySelectorAll('.rc-mobile-menu__submenu-panel.is-active');
    activeSubmenus.forEach(submenu => {
      submenu.classList.remove('is-active');
    });
  }

  setupCollapsibles() {
    // Use event delegation for collapsible toggles
    this.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-collapsible-toggle]');
      if (!toggleBtn) return;

      const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';

      // Toggle the clicked accordion
      toggleBtn.setAttribute('aria-expanded', !isExpanded);
    });
  }

  closeAllCollapsibles() {
    const collapsibleBtns = this.querySelectorAll('[data-collapsible-toggle]');
    collapsibleBtns.forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  openRewards() {
    this.close();

    let attempts = 0;
    const openJoy = () => {
      const joyButton = document.querySelector(
        '#Avada-Joy_FloatingButton button, #Avada-Joy_FloatingButton [role="button"]'
      );

      if (joyButton) {
        joyButton.click();
        return;
      }

      attempts += 1;
      if (attempts < 20) {
        window.setTimeout(openJoy, 150);
      }
    };

    window.setTimeout(openJoy, 180);
  }
}

if (!customElements.get('rc-mobile-menu')) {
  customElements.define('rc-mobile-menu', RCMobileMenu);
}

/* ==========================================================================
   Header Search
   ========================================================================== */

class RCHeaderSearch extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.debounceTimer = null;
    this.requestController = null;
    this.routesRoot = window.Shopify?.routes?.root || '/';
    this.rootPrefix = this.routesRoot === '/' ? '' : this.routesRoot.replace(/\/$/, '');
    this.isSpanish = (document.documentElement.lang || '').toLowerCase().startsWith('es');
    this.backgroundElements = new Map();
  }

  localizeStorefrontPath(url) {
    if (!url || url === '#') return url || '#';
    if (/^(https?:|mailto:|tel:|#)/.test(url) || !this.rootPrefix || !url.startsWith('/') || url.startsWith(`${this.rootPrefix}/`)) return url;
    return `${this.rootPrefix}${url}`;
  }

  connectedCallback() {
    if (this.parentElement !== document.body) {
      if (!this.dataset.rcSearchMoveScheduled) {
        this.dataset.rcSearchMoveScheduled = 'true';
        const moveToBody = () => {
          if (this.isConnected && this.parentElement !== document.body) document.body.appendChild(this);
        };
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', moveToBody, { once: true });
        else moveToBody();
      }
      return;
    }
    if (this.dataset.rcSearchBound) return;
    delete this.dataset.rcSearchMoveScheduled;
    this.dataset.rcSearchBound = 'true';
    this.closeBtn = this.querySelector('.rc-header-search__close');
    this.input = this.querySelector('.rc-header-search__input');
    this.resultsContainer = this.querySelector('.rc-header-search__results');
    this.initialResultsHTML = '';
    this.captureInitialResults();
    if (!this.id) this.id = 'rc-header-search';
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    if (!this.hasAttribute('aria-label') && !this.hasAttribute('aria-labelledby')) {
      this.setAttribute('aria-label', this.input?.getAttribute('aria-label') || (this.isSpanish ? 'Buscar productos' : 'Search products'));
    }
    this.setAttribute('tabindex', '-1');
    this.syncTriggers();
    this.closeBtn?.addEventListener('click', () => this.close());
    this.input?.addEventListener('input', (event) => this.handleInput(event));
    this.input?.addEventListener('search', (event) => this.handleInput(event));
    document.addEventListener('keydown', (event) => this.handleKeyDown(event), true);
    document.addEventListener('focusin', (event) => {
      if (this.isOpen && !this.contains(event.target)) this.focusInput();
    });
  }

  syncTriggers() {
    document.querySelectorAll('[data-rc-search-trigger], .rc-header__search-link a').forEach((trigger) => {
      trigger.setAttribute('aria-controls', this.id);
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', String(this.isOpen));
    });
  }

  focusInput() {
    (this.input || this).focus({ preventScroll: true });
  }

  open(trigger = document.activeElement) {
    if (this.isOpen) return;
    this.returnFocus = trigger instanceof HTMLElement ? trigger : null;
    this.previousOverflow = document.body.style.overflow;
    this.isOpen = true;
    this.inert = false;
    this.removeAttribute('hidden');
    this.setAttribute('open', '');
    this.captureInitialResults();
    this.syncTriggers();
    document.querySelector('.rc-header')?.classList.add('rc-header--open');
    for (const element of document.body.children) {
      if (!(element instanceof HTMLElement) || element === this || ['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)) continue;
      this.backgroundElements.set(element, element.inert);
      element.inert = true;
    }
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      if (!this.isOpen) return;
      if (this.resultsContainer && !this.resultsContainer.innerHTML.trim()) this.resultsContainer.innerHTML = this.initialResultsHTML;
      this.focusInput();
    });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.removeAttribute('open');
    this.inert = true;
    this.cancelRequest();
    this.syncTriggers();
    document.querySelector('.rc-header')?.classList.remove('rc-header--open');
    for (const [element, wasInert] of this.backgroundElements) element.inert = wasInert;
    this.backgroundElements.clear();
    document.body.style.overflow = this.previousOverflow || '';
    if (this.input) this.input.value = '';
    if (this.resultsContainer) this.resultsContainer.innerHTML = this.initialResultsHTML;
    if (this.returnFocus?.isConnected && !this.returnFocus.closest('[hidden], [inert]')) this.returnFocus.focus({ preventScroll: true });
    setTimeout(() => {
      if (!this.isOpen) this.setAttribute('hidden', '');
    }, 300);
  }

  handleKeyDown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.closest('[hidden], [inert]') && element.getClientRects().length > 0);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
      this.focus();
    } else if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !this.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  cancelRequest() {
    clearTimeout(this.debounceTimer);
    this.requestController?.abort();
    this.requestController = null;
    this.resultsContainer?.removeAttribute('aria-busy');
  }

  handleInput(event) {
    const query = event.target.value.trim();
    this.captureInitialResults();
    this.cancelRequest();
    if (query.length < 2) {
      if (this.resultsContainer) this.resultsContainer.innerHTML = this.initialResultsHTML;
      return;
    }
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 300);
  }

  captureInitialResults() {
    if (!this.initialResultsHTML && this.resultsContainer?.querySelector('[data-rc-search-initial]')) this.initialResultsHTML = this.resultsContainer.innerHTML;
  }

  async fetchResults(query) {
    const controller = new AbortController();
    this.requestController = controller;
    this.resultsContainer?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.rootPrefix}/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`, { signal: controller.signal });
      if (!response.ok) throw new Error(`Search returned ${response.status}`);
      const data = await response.json();
      if (controller.signal.aborted || this.input?.value.trim() !== query) return;
      this.renderResults(data.resources?.results?.products, query);
    } catch (error) {
      if (error.name === 'AbortError' || controller.signal.aborted || this.input?.value.trim() !== query) return;
      if (this.resultsContainer) {
        const message = this.isSpanish ? 'No se pudieron cargar las sugerencias.' : 'Suggestions could not be loaded.';
        const action = this.isSpanish ? 'Ver todos los resultados' : 'View all results';
        this.resultsContainer.innerHTML = `<div class="rc-header-search__empty"><strong role="status">${message}</strong><a href="${this.rootPrefix}/search?q=${encodeURIComponent(query)}&type=product">${action}</a></div>`;
      }
    } finally {
      if (this.requestController === controller) {
        this.resultsContainer?.removeAttribute('aria-busy');
        this.requestController = null;
      }
    }
  }

  renderResults(products, query = '') {
    if (!this.resultsContainer) return;
    if (!products?.length) {
      const emptyTitle = this.isSpanish ? 'No hemos encontrado productos' : 'No products found';
      const emptyText = this.isSpanish ? 'Prueba con otro término o descubre las últimas novedades.' : 'Try another term or explore the latest arrivals.';
      const browseText = this.isSpanish ? 'Ver novedades' : 'Shop new arrivals';
      this.resultsContainer.innerHTML = `<div class="rc-header-search__empty"><strong role="status">${emptyTitle}</strong><span>${emptyText}</span><a href="${this.rootPrefix}/collections/new-arrivals">${browseText}</a></div>`;
      return;
    }
    const viewAllText = this.isSpanish ? 'Ver todos los resultados' : 'View all results';
    const searchUrl = `${this.rootPrefix}/search?q=${encodeURIComponent(query)}&type=product`;
    this.resultsContainer.innerHTML = `<div class="rc-header-search__product-grid">${products.map((product, index) => this.renderProductCard(product, index)).join('')}</div><a class="rc-header-search__view-all" href="${searchUrl}"><span>${viewAllText}</span></a>`;
  }

  // 7-oct-2026 · Las sugerencias traen las fotos con la dirección de cdn.shopify.com, otro dominio que el navegador
  // tenía que abrir aparte (las fotos tardaban unos 5 s). La misma foto se sirve desde la tienda (/cdn/shop/…).
  sameOriginImage(value) {
    return String(value || '').replace(/^(?:https?:)?\/\/cdn\.shopify\.com\/s\/files\/1\/\d+\/\d+\/\d+\//, '/cdn/shop/');
  }

  renderProductCard(product, index = 0) {
    const image = this.sameOriginImage(product.featured_image?.url || product.image);
    const imageSrc = image ? this.imageUrlForWidth(image, 800) : '';
    const imageSrcset = image ? [168, 320, 480, 640, 800, 1000].map((width) => `${this.imageUrlForWidth(image, width)} ${width}w`).join(', ') : '';
    const price = this.escapeHtml(this.formatMoney(product.price));
    const comparePrice = Number(product.compare_at_price_max) > Number(product.price) ? this.escapeHtml(this.formatMoney(product.compare_at_price_max)) : null;
    const href = this.escapeHtml(this.localizeStorefrontPath(product.url));
    const title = this.escapeHtml(product.title || '');
    // 30-sep-2026: solo se avisa cuando está agotado (antes, «Disponible» en todos y «Sold out» sin traducir).
    const availability = product.available === false ? (this.isSpanish ? 'Agotado' : 'Sold out') : '';
    const availabilityClass = product.available === false ? ' is-sold-out' : '';
    return `<a href="${href}" class="rc-header-search__product"><div class="rc-header-search__product-image">${image ? `<img src="${this.escapeHtml(imageSrc)}" srcset="${this.escapeHtml(imageSrcset)}" sizes="(max-width: 749px) 84px, calc((100vw - 5rem) / 4)" alt="${title}" loading="${index < 4 ? 'eager' : 'lazy'}" decoding="async">` : ''}</div><div class="rc-header-search__product-info"><span class="rc-header-search__product-title">${title}</span><span class="rc-header-search__product-price">${comparePrice ? `<s>${comparePrice}</s>` : ''} ${price}</span>${availability ? `<span class="rc-header-search__availability${availabilityClass}">${availability}</span>` : ''}</div></a>`;
  }

  imageUrlForWidth(value, width) {
    const url = String(value || '');
    if (!url) return '';
    if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[character]);
  }

  formatMoney(price) {
    const amount = Number(price);
    if (!Number.isFinite(amount)) return '';
    // 30-sep-2026: mismo formato que el resto de la tienda («$45.00», «€38,00»): se copia de un precio de ejemplo
    // que pinta Liquid con el filtro money (1.234,56 en la moneda del visitante).
    const sample = this.dataset.moneySample || '';
    const parts = sample.match(/^(.*?)1([^0-9]?)234([^0-9])56(.*)$/);
    if (parts) {
      const [, before, thousands, decimals, after] = parts;
      const [whole, cents] = amount.toFixed(2).split('.');
      return before + whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + decimals + cents + after;
    }
    const currency = window.Shopify?.currency?.active || this.dataset.currency;
    const locale = document.documentElement.lang || 'en';
    if (!currency) return amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }
}

if (!customElements.get('rc-header-search')) {
  customElements.define('rc-header-search', RCHeaderSearch);
}

function initHeaderSearchTriggers() {
  if (window.rcHeaderSearchTriggersBound) return;
  window.rcHeaderSearchTriggersBound = true;
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-rc-search-trigger], .rc-header__search-link a') : null;
    if (!trigger) return;
    const search = document.querySelector('rc-header-search');
    if (!search || typeof search.open !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    const mobileMenu = document.querySelector('rc-mobile-menu');
    const returnFocus = trigger.closest('rc-mobile-menu') ? mobileMenu?.toggle : trigger;
    if (mobileMenu?.isOpen) mobileMenu.close();
    search.open(returnFocus);
  }, true);
}

initHeaderSearchTriggers();

/* ==========================================================================
   Cart Drawer Trigger
   ========================================================================== */

function initCartDrawerTrigger() {
  if (window.rcCartTriggersBound) return;
  window.rcCartTriggersBound = true;
  document.addEventListener('click', (event) => {
    const cartTrigger = event.target instanceof Element
      ? event.target.closest('[data-cart-drawer-trigger]')
      : null;
    if (!cartTrigger) return;
    const cartDrawer = document.querySelector('rc-cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  });
}

// Initialize immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawerTrigger);
} else {
  initCartDrawerTrigger();
}

/* ==========================================================================
   Cart Count Update
   ========================================================================== */

// Listen for cart updates (cart:update event from theme)
document.addEventListener('cart:update', (e) => {
  const itemCount = e.detail?.data?.itemCount;
  if (typeof itemCount === 'number') {
    updateCartCount(itemCount);
  } else {
    // Fetch current cart count
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => updateCartCount(cart.item_count))
      .catch(() => {});
  }
});

function updateCartCount(count) {
  const countElements = document.querySelectorAll('.rc-header__cart-count, [data-cart-count]');
  countElements.forEach(el => {
    el.textContent = count;
  });
}

/* ==========================================================================
   Megamenu Hover Trigger
   ========================================================================== */

function initMegamenuHover() {
  const megamenus = document.querySelectorAll('.rc-header__megamenu');
  let closeTimeout = null;

  megamenus.forEach(megamenu => {
    const summary = megamenu.querySelector('summary');
    const content = megamenu.querySelector('.rc-header__megamenu-content');
    const closeBtn = megamenu.querySelector('[data-megamenu-close]');

    if (!summary || !content) return;
    if (megamenu.dataset.rcMegamenuBound === 'true') return;
    megamenu.dataset.rcMegamenuBound = 'true';

    // Open on hover
    megamenu.addEventListener('mouseenter', () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
      // Close other open megamenus
      megamenus.forEach(other => {
        if (other !== megamenu) other.removeAttribute('open');
      });
      megamenu.setAttribute('open', '');
    });

    // Close on mouse leave with delay
    megamenu.addEventListener('mouseleave', () => {
      closeTimeout = setTimeout(() => {
        megamenu.removeAttribute('open');
      }, 150);
    });

    // Close button click
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        megamenu.removeAttribute('open');
      });
    }

    // Keep the trigger usable by mouse and keyboard. Hover still opens the
    // panel, while a click on an open trigger closes it predictably.
    summary.addEventListener('click', (e) => {
      e.preventDefault();
      const link = summary.getAttribute('data-link');
      if (link) {
        window.location.href = link;
        return;
      }

      if (megamenu.hasAttribute('open')) {
        megamenu.removeAttribute('open');
      } else {
        megamenus.forEach(other => {
          if (other !== megamenu) other.removeAttribute('open');
        });
        megamenu.setAttribute('open', '');
      }
    });
  });

  if (!window.rcMegamenuDismissBound) {
    window.rcMegamenuDismissBound = true;

    document.addEventListener('pointerdown', (event) => {
      if (event.target instanceof Element && event.target.closest('.rc-header__megamenu')) return;
      document.querySelectorAll('.rc-header__megamenu[open]').forEach(megamenu => {
        megamenu.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      document.querySelectorAll('.rc-header__megamenu[open]').forEach(megamenu => {
        megamenu.removeAttribute('open');
        megamenu.querySelector('summary')?.focus({ preventScroll: true });
      });
    });
  }
}

// Initialize on load and section render
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegamenuHover);
} else {
  initMegamenuHover();
}
document.addEventListener('shopify:section:load', initMegamenuHover);

/* ==========================================================================
   Megamenu Promo Slider
   ========================================================================== */

function initMegamenuSliders() {
  const sliderContainers = document.querySelectorAll('.rc-header__megamenu-promos--slider');

  sliderContainers.forEach(container => {
    const slider = container.querySelector('.rc-header__megamenu-slider');
    const prevBtn = container.querySelector('.rc-header__megamenu-slider-arrow--left');
    const nextBtn = container.querySelector('.rc-header__megamenu-slider-arrow--right');

    if (!slider || !prevBtn || !nextBtn) return;

    const updateArrows = () => {
      const scrollLeft = slider.scrollLeft;
      const maxScroll = slider.scrollWidth - slider.clientWidth;

      prevBtn.disabled = scrollLeft <= 0;
      nextBtn.disabled = scrollLeft >= maxScroll - 1;
    };

    const scrollAmount = () => {
      const promoCard = slider.querySelector('.rc-header__megamenu-promo');
      return promoCard ? promoCard.offsetWidth + 12 : 150; // 12 is the gap
    };

    prevBtn.addEventListener('click', () => {
      slider.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      slider.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });

    slider.addEventListener('scroll', updateArrows);

    // Initial state
    updateArrows();
  });
}

// Initialize on load and section render
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegamenuSliders);
} else {
  initMegamenuSliders();
}
document.addEventListener('shopify:section:load', initMegamenuSliders);

/* ==========================================================================
   Transparent Header Offset
   Adds padding to main content when transparent header is active
   unless the first section is a hero slider
   ========================================================================== */

(function initTransparentHeaderOffset() {
  function updateOffset() {
    const header = document.querySelector('.rc-header--transparent');
    const main = document.querySelector('main#MainContent, main.content-for-layout');

    if (!header || !main) {
      document.body.classList.remove('rc-transparent-header-active', 'rc-transparent-header-needs-offset');
      return;
    }

    // Add class to indicate transparent header is active
    document.body.classList.add('rc-transparent-header-active');

    // Check if first section is a hero that should NOT have offset
    const firstSection = main.querySelector(':scope > .shopify-section:first-child');
    const hasHero = firstSection?.querySelector('.rc-hero-slider, .rc-no-header-offset');

    if (hasHero) {
      document.body.classList.remove('rc-transparent-header-needs-offset');
    } else {
      document.body.classList.add('rc-transparent-header-needs-offset');
    }
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateOffset);
  } else {
    updateOffset();
  }

  // Also run after Shopify section renders (for theme editor)
  document.addEventListener('shopify:section:load', updateOffset);
})();

/* ==========================================================================
   Hero Slider Transparent Header Integration
   Automatically enables transparent header mode when hero slider requests it
   ========================================================================== */

(function initHeroTransparentHeader() {
  function checkHeroTransparentHeader() {
    const main = document.querySelector('main#MainContent, main.content-for-layout');
    if (!main) return;

    const firstSection = main.querySelector(':scope > .shopify-section:first-child');
    const heroWithTransparent = firstSection?.querySelector('[data-transparent-header="true"]');
    const header = document.querySelector('.rc-header');

    if (heroWithTransparent && header) {
      // Add transparent class to header if not already present
      header.classList.add('rc-header--transparent', 'rc-header--hero-transparent');
      document.body.classList.add('rc-hero-transparent-header');
    } else if (header) {
      // Remove hero-specific transparent class
      header.classList.remove('rc-header--hero-transparent');
      document.body.classList.remove('rc-hero-transparent-header');
    }
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkHeroTransparentHeader);
  } else {
    checkHeroTransparentHeader();
  }

  // Also run after Shopify section renders (for theme editor)
  document.addEventListener('shopify:section:load', checkHeroTransparentHeader);
})();

/* ==========================================================================
   Header Scroll - Show background on scroll
   ========================================================================== */

(function initHeaderScroll() {
  const scrollThreshold = 50; // px to scroll before showing background

  function handleScroll() {
    const header = document.querySelector('.rc-header');
    if (!header) return;

    if (window.scrollY > scrollThreshold) {
      header.classList.add('rc-header--scrolled');
    } else {
      header.classList.remove('rc-header--scrolled');
    }
  }

  // Throttle scroll events for performance
  let ticking = false;
  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      handleScroll(); // Check initial state
      window.addEventListener('scroll', onScroll, { passive: true });
    });
  } else {
    handleScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }
})();
